"use client";

import React, { useEffect, useState, useMemo } from "react";
import { Loader2, ShoppingCart, Check, MapPin, Phone, User, Store, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { usePageContext } from "@/components/ecoBuilder/renderer/PageContext";
import { useSiteContext } from "@/app/tenant/TenantLayoutWrapper";
import { createOrder } from "@/actions/order-actions";
import { createClient } from "@/utils/supabase/client";

import { cn } from "@/lib/utils";
import { EditorField } from "@/config/ecoBuilder/types";
import { ALGERIAN_WILAYAS } from '@/lib/data/wilayas';
import { OFFICIAL_COMMUNES } from '@/lib/data/official-locations';

import { Button } from "@/components/ecoBuilder/ui/button";
import { Input } from "@/components/ecoBuilder/ui/input";
import { Label } from "@/components/ecoBuilder/ui/label";

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ecoBuilder/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ecoBuilder/ui/popover";

import { RichTextRenderer } from "@/components/ecoBuilder/atoms/RichTextRenderer";
import { PaddingHandle } from "@/components/ecoBuilder/editor/PaddingHandle";

// ------------------------------------------------------------------
// Types & Schema
// ------------------------------------------------------------------

const phoneRegex = /^(05|06|07)[0-9]{8}$/;

export interface ExpressOrderFormProps {
    id?: string;
    style?: "default" | "minimal" | "compact";
    variant?: "default" | "minimal" | "compact";
    title?: string;
    buttonText?: string;

    // Manual overrides
    manualProductId?: string;

    // Pricing overrides
    customPrice?: number;
    offerPrice?: number;
    shippingCost?: number;

    // Toggles
    enableForm?: boolean;
    showName?: boolean;
    showPhone?: boolean;
    showWilaya?: boolean;
    showCommune?: boolean;

    // Styling
    primaryColor?: string;
    backgroundColor?: string;
    paddingTop?: number;
    paddingBottom?: number;

    // Editor Props
    isSelected?: boolean;
    onUpdateProps?: (newProps: Partial<ExpressOrderFormProps>) => void;
}

// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------

export const ExpressOrderForm: React.FC<ExpressOrderFormProps> = ({
    style,
    variant = "default",
    title = "أطلب الآن والدفع عند الاستلام",
    buttonText = "اضغط هنا للطلب",
    manualProductId,
    customPrice,
    offerPrice,
    shippingCost = 500,
    showName = true,
    showPhone = true,
    showWilaya = true,
    showCommune = true,
    primaryColor,
    backgroundColor,
    paddingTop = 60,
    paddingBottom = 60,
    isSelected,
    onUpdateProps
}) => {
    const { siteId, themeSettings } = usePageContext(); // Access theme settings
    // const { siteSlug } = useSiteContext();
    // const router = useRouter();

    const activeStyle = variant || style || "default";

    const [product, setProduct] = useState<any>(null);
    const [loadingProduct, setLoadingProduct] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dynamicShipping, setDynamicShipping] = useState(shippingCost);
    const [openWilaya, setOpenWilaya] = useState(false);
    const [openCommune, setOpenCommune] = useState(false);

    const effectiveProductId = manualProductId;
    const activePrimaryColor = primaryColor || themeSettings?.primaryColor || "#000000";

    // 1. Fetch Product Logic
    useEffect(() => {
        let isMounted = true;

        const loadProduct = async () => {
            // If manual card (no product ID) and no context, just stop loading
            const isEditor = !siteId && typeof window !== 'undefined';
            if (!effectiveProductId && !siteId && !isEditor) {
                if (isMounted) setLoadingProduct(false);
                return;
            }

            try {
                if (siteId && process.env.NEXT_PUBLIC_API_URL) {
                    try {
                        const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/v1/cms/sites/${siteId}/editor-data`);
                        if (res.ok) {
                            const json = await res.json();
                            const products = json.products || [];
                            const found = effectiveProductId
                                ? products.find((p: any) => p.id === effectiveProductId)
                                : products[0];

                            if (found && isMounted) {
                                setProduct(found);
                                setLoadingProduct(false);
                                return;
                            }
                        }
                    } catch (e) {
                        // ignore
                    }
                }

                const supabase = createClient();
                let query = supabase.from('products').select('*');
                if (effectiveProductId) {
                    query = query.eq('id', effectiveProductId);
                } else {
                    query = query.limit(1);
                }
                const { data } = await query.maybeSingle();

                if (isMounted) setProduct(data);

            } catch (err) {
                console.error("Error loading product:", err);
                if (isMounted) setFetchError("فشل تحميل بيانات المنتج");
            } finally {
                if (isMounted) setLoadingProduct(false);
            }
        };

        loadProduct();
        return () => { isMounted = false; };
    }, [effectiveProductId, siteId]);


    // 2. Schema Definition
    const checkoutSchema = useMemo(() => {
        return z.object({
            fullName: showName ? z.string().min(2, 'الاسم مطلوب') : z.string().optional(),
            phone: showPhone ? z.string().regex(phoneRegex, 'رقم الهاتف غير صحيح') : z.string().optional(),
            wilaya: showWilaya ? z.string().min(1, 'يرجى اختيار الولاية') : z.string().optional(),
            commune: showCommune ? z.string().optional() : z.string().optional(),
            quantity: z.number().min(1, 'الكمية يجب أن تكون 1 على الأقل'),
        });
    }, [showName, showPhone, showWilaya, showCommune]);

    type CheckoutFormValues = z.infer<typeof checkoutSchema>;

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors }
    } = useForm<CheckoutFormValues>({
        resolver: zodResolver(checkoutSchema),
        defaultValues: {
            quantity: 1,
            fullName: "",
            phone: "",
            wilaya: "",
            commune: ""
        }
    });

    // 3. Handlers
    const handleWilayaChange = (code: string) => {
        setValue('wilaya', code);
        setValue('commune', ''); // Reset commune
        const w = ALGERIAN_WILAYAS.find(wil => wil.code === code);
        if (w && w.shippingCost) setDynamicShipping(w.shippingCost);
        else setDynamicShipping(shippingCost);
    };

    const [isSuccess, setIsSuccess] = useState(false);

    const onSubmit: SubmitHandler<CheckoutFormValues> = async (data) => {
        if (!siteId) {
            toast.error("هذا النموذج يعمل فقط في الموقع المباشر");
            return;
        }

        setIsSubmitting(true);
        try {
            const finalPrice = offerPrice || customPrice || product?.price || 0;
            const finalTitle = product?.name || "اسم المنتج";
            const finalImage = product?.images?.[0] || product?.image_url || "/placeholder-product.jpg";

            const orderPayload = {
                customerName: data.fullName || 'Guest',
                customerPhone: data.phone || '0000000000',
                customerWilaya: data.wilaya || '',
                customerAddress: "نموذج سريع",
                customerCommune: data.commune || '',
                items: [{
                    id: product?.id || 'manual-express-item',
                    name: finalTitle,
                    price: finalPrice,
                    quantity: data.quantity,
                    image: finalImage
                }],
                subtotal: finalPrice * data.quantity,
                shippingCost: dynamicShipping,
                total: (finalPrice * data.quantity) + dynamicShipping,
                paymentMethod: 'cod' as const,
                siteId: siteId
            };

            const result = await createOrder(orderPayload);
            if (result.success) {
                toast.success("تم الطلب بنجاح!");
                reset();
                setIsSuccess(true);
            } else {
                toast.error(result.error || "خطأ في الطلب");
            }
        } catch (e) {
            toast.error("حدث خطأ غير متوقع");
        } finally {
            setIsSubmitting(false);
        }
    };

    // 4. Derived Values
    const currentQty = watch('quantity') || 1;
    const displayedPrice = offerPrice || customPrice || product?.price || 0;
    const productCurrency = product?.currency || 'DZD';
    const subtotal = displayedPrice * currentQty;
    const currentTotal = subtotal + dynamicShipping;

    if (loadingProduct && !product) {
        return <div className="p-8 flex justify-center bg-muted/10 rounded-xl border border-dashed border-border"><Loader2 className="animate-spin text-muted-foreground" /></div>;
    }

    // ------------------------------------------------------------------
    // Compact View (Sidebar)
    // ------------------------------------------------------------------
    const renderCompactStyle = () => (
        <div className={cn("p-5 rounded-2xl border border-border/80 bg-card shadow-lg", errors.phone || errors.fullName ? "ring-2 ring-red-500/10" : "")}>
            <div className="text-lg font-bold text-center mb-5 border-b pb-3" style={{ color: activePrimaryColor }}>
                <RichTextRenderer html={title} />
            </div>

            {isSuccess ? (
                <div className="p-4 text-center animate-in fade-in zoom-in duration-300">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Check className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="font-bold text-green-800 mb-1">شكراً لك!</h3>
                    <p className="text-xs text-green-700">تم استلام طلبك بنجاح.</p>
                </div>
            ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {showName && (
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-muted-foreground">الاسم</Label>
                            <Input {...register('fullName')} className="h-10 text-right bg-muted/20 pr-3" placeholder="الاسم الكامل" />
                            {errors.fullName && <p className="text-[10px] text-red-500 font-medium">{errors.fullName.message}</p>}
                        </div>
                    )}

                    {showPhone && (
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-muted-foreground">الهاتف</Label>
                            <Input {...register('phone')} dir="ltr" className="h-10 bg-muted/20 pl-3 text-left" placeholder="05 XX XX XX XX" />
                            {errors.phone && <p className="text-[10px] text-red-500 text-right font-medium">{errors.phone.message}</p>}
                        </div>
                    )}

                    <div className="flex gap-3">
                        {showWilaya && (
                            <div className="flex-1 space-y-1.5">
                                <Label className="text-xs font-semibold text-muted-foreground">الولاية</Label>
                                <Popover open={openWilaya} onOpenChange={setOpenWilaya}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={openWilaya}
                                            className="h-10 text-xs bg-muted/20 w-full justify-between px-2 font-normal"
                                        >
                                            <span className="truncate">
                                                {watch('wilaya')
                                                    ? ALGERIAN_WILAYAS.find((w) => w.code === watch('wilaya'))?.name
                                                        ? `${watch('wilaya')}-${ALGERIAN_WILAYAS.find((w) => w.code === watch('wilaya'))?.name}`
                                                        : watch('wilaya')
                                                    : "اختر..."}
                                            </span>
                                            <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[180px] p-0" align="start" dir="rtl">
                                        <Command>
                                            <CommandInput placeholder="بحث..." className="text-xs text-right" dir="rtl" />
                                            <CommandList>
                                                <CommandEmpty>لا يوجد.</CommandEmpty>
                                                <CommandGroup>
                                                    {ALGERIAN_WILAYAS.map((w) => (
                                                        <CommandItem
                                                            key={w.code}
                                                            value={`${w.code} ${w.name}`}
                                                            onSelect={() => {
                                                                handleWilayaChange(w.code);
                                                                setOpenWilaya(false);
                                                            }}
                                                            className="text-xs flex justify-between items-center"
                                                        >
                                                            <span>{w.code}-{w.name}</span>
                                                            <Check
                                                                className={cn(
                                                                    "mr-1 h-3 w-3",
                                                                    watch('wilaya') === w.code ? "opacity-100" : "opacity-0"
                                                                )}
                                                                style={{ color: activePrimaryColor }}
                                                            />
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                {errors.wilaya && <p className="text-[10px] text-red-500">{errors.wilaya.message}</p>}
                            </div>
                        )}
                        <div className="w-20 space-y-1.5">
                            <Label className="text-xs font-semibold text-muted-foreground">الكمية</Label>
                            <Input
                                type="number"
                                min={1}
                                {...register('quantity', { valueAsNumber: true })}
                                className="h-10 text-center p-0 bg-muted/20"
                            />
                        </div>
                    </div>

                    <div className="pt-3 border-t border-dashed mt-2 space-y-1">
                        <div className="flex justify-between items-center text-xs text-muted-foreground">
                            <span>سعر المنتج:</span>
                            <span>{subtotal.toLocaleString()} {productCurrency}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs text-muted-foreground">
                            <span>التوصيل:</span>
                            <span>{dynamicShipping > 0 ? `${dynamicShipping} ${productCurrency}` : 'مجاني'}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm font-extrabold pt-1">
                            <span>الإجمالي:</span>
                            <span style={{ color: activePrimaryColor }}>{currentTotal.toLocaleString()} {productCurrency}</span>
                        </div>
                    </div>

                    <Button type="submit" className="w-full gap-2 mt-2 h-11" style={{ backgroundColor: activePrimaryColor, color: '#fff' }} disabled={isSubmitting}>
                        {isSubmitting ? <Loader2 className="animate-spin w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
                        <RichTextRenderer html={buttonText} />
                    </Button>
                </form>
            )}
        </div>
    );

    // ------------------------------------------------------------------
    // Minimal View (RE-DESIGNED)
    // ------------------------------------------------------------------
    const renderMinimalStyle = () => (
        <div className="card-minimal max-w-2xl mx-auto py-10 px-2 lg:px-0">
            <div className="text-center mb-10 space-y-2">
                <div className="text-4xl font-extrabold tracking-tight">
                    <RichTextRenderer html={title} />
                </div>
                <div className="w-20 h-1.5 rounded-full mx-auto opacity-20" style={{ backgroundColor: activePrimaryColor }} />
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

                {/* Row 1: Name & Phone - Clean Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {showName && (
                        <div className="relative group pt-2">
                            <Input
                                {...register('fullName')}
                                className="peer h-14 px-5 pt-4 pb-1 w-full bg-muted/10 border-border/80 rounded-xl focus:ring-0 focus:border-primary transition-all text-right shadow-sm placeholder-transparent"
                                placeholder=" "
                                id="minimal-name"
                            />
                            <Label
                                htmlFor="minimal-name"
                                className="absolute right-5 start-auto top-4 z-10 origin-right transition-all duration-200 pointer-events-none text-muted-foreground
                                           peer-focus:-translate-y-3 peer-focus:scale-[0.8] peer-focus:text-primary
                                           peer-[:not(:placeholder-shown)]:-translate-y-3 peer-[:not(:placeholder-shown)]:scale-[0.8] peer-[:not(:placeholder-shown)]:text-primary"
                            >
                                الاسم الكامل
                            </Label>
                        </div>
                    )}

                    {showPhone && (
                        <div className="relative group pt-2">
                            <Input
                                {...register('phone')}
                                className="peer h-14 px-5 pt-4 pb-1 w-full bg-muted/10 border-border/80 rounded-xl focus:ring-0 focus:border-primary transition-all text-right shadow-sm placeholder-transparent"
                                placeholder=" "
                                id="minimal-phone"
                                dir="rtl"
                            />
                            <Label
                                htmlFor="minimal-phone"
                                className="absolute right-5 start-auto top-4 z-10 origin-right transition-all duration-200 pointer-events-none text-muted-foreground
                                           peer-focus:-translate-y-3 peer-focus:scale-[0.8] peer-focus:text-primary
                                           peer-[:not(:placeholder-shown)]:-translate-y-3 peer-[:not(:placeholder-shown)]:scale-[0.8] peer-[:not(:placeholder-shown)]:text-primary"
                            >
                                رقم الهاتف
                            </Label>
                            <div className="absolute left-4 top-4 text-muted-foreground/30"><Phone className="w-5 h-5 pointer-events-none" /></div>

                            {errors.phone && <p className="text-xs text-red-500 mt-1 mr-2 font-medium animate-in fade-in slide-in-from-right-1">{errors.phone.message}</p>}
                        </div>
                    )}
                </div>

                {/* Row 2: Wilaya & Commune - Consistent Height & Spacing */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {showWilaya && (
                        <div className="relative group pt-2">
                            <Popover open={openWilaya} onOpenChange={setOpenWilaya}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={openWilaya}
                                        className="h-14 px-5 w-full bg-muted/10 border-border/80 rounded-xl hover:border-primary focus:ring-0 shadow-sm text-right justify-between font-normal hover:bg-muted/20 text-base"
                                        id="minimal-wilaya"
                                    >
                                        <span className="truncate">
                                            {watch('wilaya')
                                                ? ALGERIAN_WILAYAS.find((w) => w.code === watch('wilaya'))?.name
                                                    ? `${watch('wilaya')} - ${ALGERIAN_WILAYAS.find((w) => w.code === watch('wilaya'))?.name}`
                                                    : watch('wilaya')
                                                : "اختر الولاية"}
                                        </span>
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[300px] p-0" align="end" dir="rtl">
                                    <Command>
                                        <CommandInput placeholder="بحث عن ولاية..." className="text-right" dir="rtl" />
                                        <CommandList>
                                            <CommandEmpty>لا توجد ولاية بهذا الاسم.</CommandEmpty>
                                            <CommandGroup>
                                                {ALGERIAN_WILAYAS.map((w) => (
                                                    <CommandItem
                                                        key={w.code}
                                                        value={`${w.code} ${w.name}`}
                                                        onSelect={() => {
                                                            handleWilayaChange(w.code);
                                                            setOpenWilaya(false);
                                                        }}
                                                        className="flex justify-between items-center"
                                                    >
                                                        <span className="text-right">{w.code} - {w.name}</span>
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                watch('wilaya') === w.code ? "opacity-100" : "opacity-0"
                                                            )}
                                                            style={{ color: activePrimaryColor }}
                                                        />
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                            {errors.wilaya && <p className="text-xs text-red-500 mt-1 mr-2 font-medium">{errors.wilaya.message}</p>}
                        </div>
                    )}

                    {showCommune && (
                        <div className="relative group pt-2">
                            <Popover open={openCommune} onOpenChange={setOpenCommune}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={openCommune}
                                        className="h-14 px-5 w-full bg-muted/10 border-border/80 rounded-xl hover:border-primary focus:ring-0 shadow-sm text-right justify-between font-normal hover:bg-muted/20 text-base"
                                        id="minimal-commune"
                                        disabled={!watch('wilaya')}
                                    >
                                        <span className="truncate">
                                            {watch('commune')
                                                ? watch('commune')
                                                : "اختر البلدية"}
                                        </span>
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[300px] p-0" align="end" dir="rtl">
                                    <Command>
                                        <CommandInput placeholder="بحث عن بلدية..." className="text-right" dir="rtl" />
                                        <CommandList>
                                            <CommandEmpty>لا توجد بلدية.</CommandEmpty>
                                            <CommandGroup>
                                                {(() => {
                                                    const wCode = watch('wilaya');
                                                    if (!wCode) return null;
                                                    const key = parseInt(wCode).toString();
                                                    const list = OFFICIAL_COMMUNES[key] || [];
                                                    return list.map((c: string) => (
                                                        <CommandItem
                                                            key={c}
                                                            value={c}
                                                            onSelect={() => {
                                                                setValue('commune', c); // Set exact casing from list
                                                                setOpenCommune(false);
                                                            }}
                                                            className="flex justify-between items-center"
                                                        >
                                                            <span className="text-right">{c}</span>
                                                            <Check
                                                                className={cn(
                                                                    "mr-2 h-4 w-4",
                                                                    watch('commune') === c ? "opacity-100" : "opacity-0"
                                                                )}
                                                                style={{ color: activePrimaryColor }}
                                                            />
                                                        </CommandItem>
                                                    ));
                                                })()}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                            <div className="absolute left-4 top-4 text-muted-foreground/30"><Store className="w-5 h-5 pointer-events-none" /></div>
                        </div>
                    )}
                </div>

                {/* Row 3: Styling Separator & Quantity */}
                <div className="flex items-center gap-4 py-2">
                    <div className="h-px bg-border flex-1"></div>
                    <span className="text-muted-foreground text-sm font-medium bg-background px-2">معلومات الطلب</span>
                    <div className="h-px bg-border flex-1"></div>
                </div >

                <div className="flex flex-col sm:flex-row gap-5 items-stretch">
                    {/* Quantity Block */}
                    <div className="sm:w-1/3 flex items-center justify-between border border-border rounded-xl px-4 py-2 bg-background shadow-sm hover:border-primary/50 transition-colors h-24">
                        <div className="flex flex-col items-center gap-1 w-full">
                            <span className="text-xs text-muted-foreground font-semibold">الكمية</span>
                            <div className="flex items-center gap-4 mt-1">
                                <button type="button" onClick={() => setValue('quantity', Math.max(1, currentQty - 1))} className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-primary hover:text-white transition-colors">-</button>
                                <span className="font-bold text-2xl">{currentQty}</span>
                                <button type="button" onClick={() => setValue('quantity', currentQty + 1)} className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-primary hover:text-white transition-colors">+</button>
                            </div>
                        </div>
                        <input type="hidden" {...register('quantity', { valueAsNumber: true })} />
                    </div>

                    {/* Price Summary Block */}
                    <div className="sm:w-2/3 bg-muted/20 border border-border rounded-xl p-5 flex flex-col justify-center h-24 relative overflow-hidden group">
                        <div className="absolute right-0 top-0 w-1 h-full transition-all group-hover:w-1.5" style={{ backgroundColor: activePrimaryColor }}></div>
                        <div className="flex justify-between items-end relative z-10 px-2">
                            <div className="flex flex-col">
                                <span className="text-sm text-muted-foreground mb-1">المجموع النهائي للطلب</span>
                                <span className="text-xs px-2 py-0.5 rounded-md bg-green-100 text-green-700 w-fit font-bold dark:bg-green-900/30 dark:text-green-400">
                                    {dynamicShipping > 0 ? 'توصيل لباب المنزل' : 'توصيل مجاني شامل'}
                                </span>
                            </div>
                            <div className="text-right">
                                <span className="block text-3xl font-black tracking-tight leading-none" style={{ color: activePrimaryColor }}>
                                    {currentTotal.toLocaleString()}
                                    <span className="text-sm font-medium text-muted-foreground mr-1">{productCurrency}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <Button
                    type="submit"
                    className="w-full h-14 text-lg font-bold shadow-lg shadow-primary/5 rounded-xl hover:shadow-xl hover:translate-y-[-1px] transition-all"
                    style={{ backgroundColor: activePrimaryColor, color: '#fff' }}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? <Loader2 className="animate-spin" /> : "تأكيد واستلام الطلب"}
                </Button>
            </form >
        </div >
    );

    // ------------------------------------------------------------------
    // Default View (Boxed)
    // ------------------------------------------------------------------
    const renderDefaultStyle = () => (
        <div className="bg-background border border-border rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300" dir="rtl">
            <div className="bg-muted/30 p-5 text-center border-b border-border/60">
                <div className="inline-flex items-center justify-center gap-2 w-full">
                    <span className="text-2xl animate-bounce duration-1000">👇</span>
                    <div className="text-xl font-bold mx-2" style={{ color: activePrimaryColor }}>
                        <RichTextRenderer html={title} />
                    </div>
                    <span className="text-2xl animate-bounce duration-1000 delay-100">👇</span>
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
                {fetchError && <p className="text-sm text-red-500 text-center bg-red-50 p-2 rounded-lg font-medium">{fetchError}</p>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {showName && (
                        <div className="space-y-2">
                            <Label className="text-right block w-full text-base font-semibold">الاسم بالكامل</Label>
                            <div className="relative">
                                {/* Added pr-10 for RTL spacing, Icon on Right */}
                                <Input {...register('fullName')} className="h-14 text-right pr-10 pl-4 text-base shadow-sm" placeholder="أدخل اسمك" />
                                <div className="absolute right-3 top-3.5 text-muted-foreground/40 pointer-events-none"><User className="w-5 h-5" /></div>
                            </div>
                            {errors.fullName && <p className="text-xs text-red-500 font-medium">{errors.fullName.message}</p>}
                        </div>
                    )}

                    {showPhone && (
                        <div className="space-y-2">
                            <Label className="text-right block w-full text-base font-semibold">رقم الهاتف</Label>
                            <div className="relative">
                                {/* Changed to text-right for RTL Arabic typing, Icon on Right */}
                                <Input {...register('phone')} className="h-14 text-right pr-10 pl-4 text-base shadow-sm" placeholder="05 XX XX XX XX" dir="rtl" />
                                <div className="absolute right-3 top-3.5 text-muted-foreground/40 pointer-events-none"><Phone className="w-5 h-5" /></div>
                            </div>
                            {errors.phone && <p className="text-xs text-red-500 text-right font-medium">{errors.phone.message}</p>}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {showWilaya && (
                        <div className="space-y-2">
                            <Label className="text-right block w-full text-base font-semibold">الولاية</Label>
                            <div className="relative">
                                <Popover open={openWilaya} onOpenChange={setOpenWilaya}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={openWilaya}
                                            className="h-14 w-full text-base shadow-sm pr-10 pl-4 text-right justify-between font-normal hover:bg-background"
                                            dir="rtl"
                                        >
                                            <span className="truncate">
                                                {watch('wilaya')
                                                    ? ALGERIAN_WILAYAS.find((w) => w.code === watch('wilaya'))?.name
                                                        ? `${watch('wilaya')} - ${ALGERIAN_WILAYAS.find((w) => w.code === watch('wilaya'))?.name}`
                                                        : watch('wilaya')
                                                    : "اختر ولايتك"}
                                            </span>
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="center" dir="rtl">
                                        <Command>
                                            <CommandInput placeholder="بحث عن ولاية..." className="text-right" dir="rtl" />
                                            <CommandList>
                                                <CommandEmpty>لم يتم العثور على الولاية.</CommandEmpty>
                                                <CommandGroup>
                                                    {ALGERIAN_WILAYAS.map((w) => (
                                                        <CommandItem
                                                            key={w.code}
                                                            value={`${w.code} ${w.name}`}
                                                            onSelect={() => {
                                                                handleWilayaChange(w.code);
                                                                setOpenWilaya(false);
                                                            }}
                                                            className="flex justify-between items-center"
                                                        >
                                                            <span className="text-right">{w.code} - {w.name}</span>
                                                            <Check
                                                                className={cn(
                                                                    "mr-2 h-4 w-4",
                                                                    watch('wilaya') === w.code ? "opacity-100" : "opacity-0"
                                                                )}
                                                                style={{ color: activePrimaryColor }}
                                                            />
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                <div className="absolute left-3 top-3.5 text-muted-foreground/40 pointer-events-none z-10"><MapPin className="w-5 h-5" /></div>
                            </div>
                            {errors.wilaya && <p className="text-xs text-red-500 font-medium">{errors.wilaya.message}</p>}
                        </div>
                    )}

                    {showCommune && (
                        <div className="space-y-2">
                            <Label className="text-right block w-full text-base font-semibold">البلدية</Label>
                            <div className="relative">
                                <Popover open={openCommune} onOpenChange={setOpenCommune}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={openCommune}
                                            className="h-14 w-full text-base shadow-sm pr-10 pl-4 text-right justify-between font-normal hover:bg-background"
                                            dir="rtl"
                                            disabled={!watch('wilaya')}
                                        >
                                            <span className="truncate">
                                                {watch('commune')
                                                    ? watch('commune')
                                                    : "اختر البلدية"}
                                            </span>
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="center" dir="rtl">
                                        <Command>
                                            <CommandInput placeholder="بحث عن بلدية..." className="text-right" dir="rtl" />
                                            <CommandList>
                                                <CommandEmpty>لم يتم العثور على البلدية.</CommandEmpty>
                                                <CommandGroup>
                                                    {(() => {
                                                        const wCode = watch('wilaya');
                                                        if (!wCode) return null;
                                                        const key = parseInt(wCode).toString();
                                                        const list = OFFICIAL_COMMUNES[key] || [];
                                                        return list.map((c: string) => (
                                                            <CommandItem
                                                                key={c}
                                                                value={c}
                                                                onSelect={() => {
                                                                    setValue('commune', c);
                                                                    setOpenCommune(false);
                                                                }}
                                                                className="flex justify-between items-center"
                                                            >
                                                                <span className="text-right">{c}</span>
                                                                <Check
                                                                    className={cn(
                                                                        "mr-2 h-4 w-4",
                                                                        watch('commune') === c ? "opacity-100" : "opacity-0"
                                                                    )}
                                                                    style={{ color: activePrimaryColor }}
                                                                />
                                                            </CommandItem>
                                                        ));
                                                    })()}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                <div className="absolute right-3 top-3.5 text-muted-foreground/40 pointer-events-none"><Store className="w-5 h-5" /></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Quantity and Pricing Breakdown */}
                <div className="bg-muted/20 p-5 rounded-xl border border-border space-y-4">
                    {/* Row 1: Quantity Control */}
                    <div className="flex items-center justify-between pb-4 border-b border-border/50">
                        <span className="font-bold text-base text-muted-foreground">الكمية المطلوبة:</span>
                        <div className="flex items-center gap-3 bg-background rounded-lg shadow-sm border p-1">
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 hover:bg-muted"
                                onClick={() => {
                                    const c = watch('quantity');
                                    if (c > 1) setValue('quantity', c - 1);
                                }}
                            >-</Button>
                            <Input
                                {...register('quantity', { valueAsNumber: true })}
                                className="w-16 text-center h-9 font-bold border-none text-lg p-0 focus-visible:ring-0"
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 hover:bg-muted"
                                onClick={() => {
                                    const c = watch('quantity');
                                    setValue('quantity', c + 1);
                                }}
                            >+</Button>
                        </div>
                    </div>

                    {/* Row 2: Price Breakdown */}
                    <div className="space-y-2 pt-1">
                        <div className="flex justify-between items-center text-sm text-muted-foreground">
                            <span>سعر المنتج ({currentQty} قطع):</span>
                            <span>{subtotal.toLocaleString()} {productCurrency}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm text-muted-foreground">
                            <span>تكلفة التوصيل:</span>
                            <span className={cn(dynamicShipping === 0 && "text-green-600 font-bold")}>
                                {dynamicShipping > 0 ? `${dynamicShipping} ${productCurrency}` : 'مجاني'}
                            </span>
                        </div>
                    </div>

                    {/* Row 3: Grand Total */}
                    <div className="flex justify-between items-center pt-2 border-t border-dashed border-border/60">
                        <span className="text-lg font-bold">المجموع الكلي:</span>
                        <span className="text-2xl font-black tracking-tight" style={{ color: activePrimaryColor }}>
                            {currentTotal.toLocaleString()} {productCurrency}
                        </span>
                    </div>
                </div>

                <Button
                    type="submit"
                    className="w-full h-16 text-white text-xl font-bold rounded-xl shadow-lg hover:shadow-2xl transition-all hover:scale-[1.01] active:scale-[0.99] duration-300"
                    style={{ backgroundColor: activePrimaryColor }}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? <Loader2 className="animate-spin" /> :
                        <div className="w-full flex items-center justify-center gap-3">
                            <span className="text-2xl">👈</span>
                            <RichTextRenderer html={buttonText} inline />
                            <span className="text-2xl">👉</span>
                        </div>
                    }
                </Button>

                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground/80 pt-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse ring-4 ring-green-500/20"></div>
                    <span className="font-medium">أكثر من {Math.floor(Math.random() * 50) + 10} شخص يشاهد هذا العرض حالياً</span>
                </div>

            </form>
        </div>
    );


    // ------------------------------------------------------------------
    // Main Render Switch
    // ------------------------------------------------------------------

    const handlePaddingChange = (side: 'top' | 'bottom') => (newVal: number) => {
        if (!onUpdateProps) return;
        if (side === 'top') onUpdateProps({ paddingTop: newVal });
        else onUpdateProps({ paddingBottom: newVal });
    };

    return (
        <section
            className={cn(
                "w-full transition-all duration-300 relative group",
                backgroundColor && `bg-[${backgroundColor}]`
            )
            }
            style={{
                paddingTop: `${paddingTop}px`,
                paddingBottom: `${paddingBottom}px`,
            }}
            dir="rtl"
        >
            <div className="container mx-auto px-4 relative z-20">
                {activeStyle === 'compact' && renderCompactStyle()}
                {activeStyle === 'minimal' && renderMinimalStyle()}
                {/* Fallback to default if activeStyle is invalid or default */}
                {(!activeStyle || activeStyle === 'default' || !['compact', 'minimal'].includes(activeStyle)) && renderDefaultStyle()}
            </div>

            {/* Padding Handles (Only when selected) */}
            {isSelected && onUpdateProps && (
                <>
                    <PaddingHandle
                        side="top"
                        currentPadding={paddingTop}
                        onPaddingChange={handlePaddingChange('top')}
                    />
                    <PaddingHandle
                        side="bottom"
                        currentPadding={paddingBottom}
                        onPaddingChange={handlePaddingChange('bottom')}
                    />
                </>
            )}
        </section>
    );
}


// ------------------------------------------------------------------
// REGISTRY EXPORTS
// ------------------------------------------------------------------

export const ExpressOrderFormDefaultProps = {
    title: "أضف معلوماتك في الأسفل للطلب",
    buttonText: "اضغط هنا للطلب",
    variant: "default",
    shippingCost: 500,
    showName: true,
    showPhone: true,
    showWilaya: true,
    showCommune: true,
    paddingTop: 60,
    paddingBottom: 60,
};

export const ExpressOrderFormSchema: EditorField[] = [
    {
        key: "manualProductId",
        label: "المنتج المستهدف",
        type: "product",
        description: "اختر المنتج المراد بيعه في هذا النموذج."
    },
    {
        key: "variant",
        label: "تخطيط النموذج",
        type: "select",
        options: [
            { value: "default", label: "📦 الافتراضي (صندوق)" },
            { value: "minimal", label: "✨ بسيط (Minimal)" },
            { value: "compact", label: "📱 مضغوط (Sidebar)" }
        ],
        defaultValue: "default"
    },
    {
        key: "title",
        label: "عنوان النموذج",
        type: "richtext",
        defaultValue: "أضف معلوماتك في الأسفل للطلب"
    },
    {
        key: "buttonText",
        label: "نص الزر",
        type: "richtext",
        defaultValue: "اضغط هنا للطلب"
    },
    {
        key: "offerPrice",
        label: "سعر العرض (اختياري)",
        type: "number",
        description: "تجاوز سعر المنتج الأصلي."
    },
    {
        key: "shippingCost",
        label: "سعر التوصيل الافتراضي",
        type: "number",
        defaultValue: 500
    },
    {
        key: "showName",
        label: "إظهار حقل الاسم",
        type: "boolean",
        defaultValue: true
    },
    {
        key: "showPhone",
        label: "إظهار حقل الهاتف",
        type: "boolean",
        defaultValue: true
    },
    {
        key: "showWilaya",
        label: "إظهار حقل الولاية",
        type: "boolean",
        defaultValue: true
    },
    {
        key: "showCommune",
        label: "إظهار حقل البلدية",
        type: "boolean",
        defaultValue: true
    },
    {
        key: "primaryColor",
        label: "لون الزر (اختياري)",
        type: "color",
        description: "اتركه فارغاً لاستخدام لون الثيم"
    },
    {
        key: "paddingTop",
        label: "مسافة علوية",
        type: "slider",
        defaultValue: 60,
        sliderConfig: { min: 0, max: 200, step: 4, unit: "px" }
    },
    {
        key: "paddingBottom",
        label: "مسافة سفلية",
        type: "slider",
        defaultValue: 60,
        sliderConfig: { min: 0, max: 200, step: 4, unit: "px" }
    }
];
